// src/redux/services/SettlementsApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  TSettlementModel,
  TCreateSettlementRequest,
} from '../types/SettlementsTypes';
import { baseQuery, getBaseQueryWithReauth } from '../baseQueryAuth';
import { merchantCabinetApi } from '../../features/merchant-cabinet/api';

// ─── 1) pull the params out into their own alias ──────────────────────────
export type TDownloadSettlementsRequest = {
  direction: 'M2P' | 'P2M' | 'all';
  status?: string;
  date_from?: string;
  date_to?: string;
};

export const SettlementsApi = createApi({
  reducerPath: 'SettlementsApi',
  baseQuery: getBaseQueryWithReauth(baseQuery),
  tagTypes: ['Settlements'],
  endpoints: (build) => ({
    // GET /api/v1/merchant/settlements/
    getSettlements: build.query<TSettlementModel[], void>({
      query: () => ({
        url: '/merchant/settlements/',
        method: 'GET',
      }),
      providesTags: ['Settlements'],
    }),

    // POST /api/v1/merchant/settlements/
    createSettlement: build.mutation<TSettlementModel, TCreateSettlementRequest>({
      query: (body) => ({
        url: '/merchant/create_settlements/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Settlements'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(merchantCabinetApi.util.invalidateTags(['Dashboard', 'Settlements']));
        } catch {}
      },
    }),

    // GET /api/v1/merchant/platform_wallet_addresses/
    getPlatformWalletAddresses: build.query<string[], void>({
      query: () => ({
        url: '/merchant/platform_wallet_addresses/',
        method: 'GET',
      }),
      providesTags: ['Settlements'], // reuse tag or define new if needed
    }),
    // ─── 2) single-line generic, no inline object ─────────────────────────
    // 2) Скачать выписки в Excel
    downloadSettlements: build.query<
      Blob,
      {
        direction: 'M2P' | 'P2M' | 'all';
        status?: string;
        date_from?: string;
        date_to?: string;
      }
    >({
      query: ({ direction, status, date_from, date_to }) => {
        const params = new URLSearchParams();
        params.append('direction', direction);
        if (status)    params.append('status', status);
        if (date_from) params.append('date_from', date_from);
        if (date_to)   params.append('date_to', date_to);

        return {
          url: `merchant/download_settlements/?${params.toString()}`,
          method: 'GET',
          // ПЕРЕМЕСТИЛИ responseHandler ВНУТРИ query-return
          responseHandler: async (response: Response) => {
            if (!response.ok) {
              throw new Error('Failed to download settlements');
            }
            return response.blob();
          },
        };
      },
    }),
  }),
});

export const {
  useGetSettlementsQuery,
  useCreateSettlementMutation,
  useGetPlatformWalletAddressesQuery,
  useLazyDownloadSettlementsQuery,
} = SettlementsApi;
